// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback, useMemo } from 'react';
import { formatDate } from '@loa/theme';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Alert, Modal as FinModal, AlertInfoPanel, AppIcon, Spinner, Button, ViewLoAModal } from '@loa/ui-components';
import { policyAggregateApi } from '../api/policyAggregateApi';
import { policyApi } from '../api/policyApi';
import { policyDocumentApi } from '../api/policyDocumentApi';
import { providerSubmissionApi } from '../api/providerSubmissionApi';
import { clientDocumentApi, type ClientUploadedDocumentDTO } from '../api/clientDocumentApi';
import type { PolicyDetailDTO, PolicySignatoryDTO } from '../types/policyList';
import type { Policy, PolicyCreateDTO, PolicyUpdateDTO } from '../types/policy';
import { PolicyDocumentUploader } from '../components/PolicyDocumentUploader';
import { PolicyDocumentsTable } from '../components/PolicyDocumentsTable';
import type { DocumentRow } from '../components/PolicyDocumentsTable';
import { PolicyMetadataTab } from '../components/PolicyMetadataTab';
import { PolicySignedDocumentsTab } from '../components/policy/PolicySignedDocumentsTab';
import { PolicyForm } from '../components/PolicyForm';
import { SignatoryStatusBadge, ResolveFlagModal, RePublishModal } from '../components/signatoryStatus';
import { PolicyOverrideIndicator, PolicyOverrideModal } from '../components/policyOverride';
import CombinedPolicyEditModal from '../components/CombinedPolicyEditModal';
import { DeletePolicyModal } from '../components/policy/DeletePolicyModal';
import { ProviderSubmissionsTab } from '../components/providerAccess';
import { useProviderSubmissions } from '../hooks/useProviderSubmissions';
import { useProviderStatusHistory } from '../hooks/useProviderStatusHistory';
import { usePolicyDocuments } from '../hooks/usePolicyDocuments';
import { PolicyDocumentStatus } from '../types/policyDocument';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { policyMetadataApi } from '../api/policyMetadataApi';
import type { PolicyMetadataExistsResponse } from '../types/policyMetadata';
import { MoreOptionsButton } from '../components/MoreOptionsButton';
import { TabBar } from '../components/TabBar';
import { getRejectionReasonLabel } from '../utils/rejectionReasonDisplay';
import { useAdvisor } from '../contexts/AdvisorContext';
import { usePageContextRegister } from '../contexts/PageContextProvider';
import { EntityTagEditor } from '../components/tags/EntityTagEditor';
import { useEntityTags } from '../hooks/useEntityTags';
import './PolicyDetailPage.css';
import '../components/loa/LOATableCard.css';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The ViewLoAModal iframe can only preview PDFs and images inline. */
const isPreviewableContentType = (contentType?: string): boolean =>
  contentType === 'application/pdf' || (contentType?.startsWith('image/') ?? false);

// Get initials from provider name
function getProviderInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

// Format currency helper
function formatCurrency(amount?: number): string {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

// Get ownership display

// Get relationship badge class
function getRelationshipClass(type: string): string {
  const classMap: Record<string, string> = {
    OWNER: 'owner',
    SIGNATORY: 'signatory',
    OWNER_AND_SIGNATORY: 'owner_and_signatory',
  };
  return classMap[type] || 'owner';
}

// Format relationship type
function formatRelationshipType(type: string): string {
  const displayMap: Record<string, string> = {
    OWNER: 'Owner',
    SIGNATORY: 'Signatory',
    OWNER_AND_SIGNATORY: 'Owner & Signatory',
  };
  return displayMap[type] || type;
}

export function PolicyDetailPage() {
  const { policyId } = useParams<{ policyId: string }>();
  const navigate = useNavigate();
  const { setBreadcrumbItems, setBackTo } = usePageHeader();
  const { advisor } = useAdvisor();
  const [activeTab, setActiveTab] = useState<'overview' | 'metadata' | 'loas' | 'documents' | 'log'>('overview');
  const [policy, setPolicy] = useState<PolicyDetailDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  usePageContextRegister({
    pageType: 'POLICY_DETAIL',
    entityIds: policyId
      ? {
          policyId,
          profileId: policy?.profileId ?? undefined,
        }
      : undefined,
    pageTitle: policy?.policyNumber
      ? `${policy.providerName ?? ''} ${policy.policyNumber}`.trim()
      : undefined,
  });

  // Tag chips + picker on the policy header (Tags PRD §9).
  const { tagsByEntityId } = useEntityTags('POLICY', policyId ? [policyId] : []);
  const policyTags = policyId ? tagsByEntityId[policyId] ?? [] : [];

  // Resolve UUID to display name for legacy data (before backend stored names)
  const resolveUser = useMemo(() => {
    const advisorName = advisor ? `${advisor.firstName} ${advisor.lastName}` : undefined;
    const keycloakId = advisor?.keycloakUserId;
    return (value: string): string => {
      if (!value || value === 'Provider' || value === 'System') return value;
      if (UUID_REGEX.test(value) && keycloakId && value === keycloakId && advisorName) {
        return advisorName;
      }
      return value;
    };
  }, [advisor]);

  // Delete policy state
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Document viewer state
  const [viewDocState, setViewDocState] = useState<{ title: string } | null>(null);
  const [viewDocUrl, setViewDocUrl] = useState<string | null>(null);
  const [viewDocLoading, setViewDocLoading] = useState(false);

  const handleViewDocument = useCallback(async (row: DocumentRow) => {
    const parts = [
      policy?.providerName,
      policy?.policyTypeName,
      policy?.policyNumber,
    ].filter(Boolean);
    const title = parts.length > 0 ? parts.join(' - ') : row.filename;
    setViewDocState({ title });
    setViewDocLoading(true);
    setViewDocUrl(null);
    try {
      let blob: Blob;
      if (row.source === 'advisor' && row.version != null && policyId) {
        const result = await policyDocumentApi.downloadVersion(policyId, row.version);
        blob = result.blob;
      } else if (row.source === 'provider' && row.submissionId && policyId) {
        blob = await providerSubmissionApi.downloadDocument(policyId, row.submissionId);
      } else if (row.source === 'client-upload' && row.documentId) {
        const result = await clientDocumentApi.download(row.documentId);
        blob = result.blob;
        // Client uploads can be non-PDF (Word/Excel). The modal iframe only
        // previews PDF/images — open anything else in a new tab instead.
        if (!isPreviewableContentType(row.contentType)) {
          const url = URL.createObjectURL(blob);
          globalThis.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 30000);
          setViewDocState(null);
          return;
        }
      } else {
        return;
      }
      setViewDocUrl(URL.createObjectURL(blob));
    } catch {
      // Error keeps viewDocUrl null, modal will show error state
    } finally {
      setViewDocLoading(false);
    }
  }, [policyId, policy?.providerName, policy?.policyTypeName, policy?.policyNumber]);

  const handleCloseDocViewer = useCallback(() => {
    if (viewDocUrl) URL.revokeObjectURL(viewDocUrl);
    setViewDocUrl(null);
    setViewDocState(null);
  }, [viewDocUrl]);

  // Document management hook
  const {
    versions: documentVersions,
    loading: documentsLoading,
    error: documentsError,
    hasDocuments,
    versionCount,
    documentStatus,
    downloadVersion,
    refresh: refreshDocuments
  } = usePolicyDocuments(policyId || '');

  // Provider submissions for Documents tab
  const {
    data: providerSubmissions,
    loading: providerSubmissionsLoading,
    downloadDocument: downloadProviderDocument,
  } = useProviderSubmissions(policyId || '');

  // Client-uploaded documents linked to this policy (Documents tab).
  const [clientUploads, setClientUploads] = useState<ClientUploadedDocumentDTO[]>([]);
  const [clientUploadsLoading, setClientUploadsLoading] = useState(false);

  useEffect(() => {
    if (!policyId) return;
    let cancelled = false;
    setClientUploadsLoading(true);
    clientDocumentApi
      .listByPolicy(policyId)
      .then((docs) => { if (!cancelled) setClientUploads(docs); })
      .catch(() => { if (!cancelled) setClientUploads([]); })
      .finally(() => { if (!cancelled) setClientUploadsLoading(false); });
    return () => { cancelled = true; };
  }, [policyId]);

  const handleDownloadClientUpload = useCallback(
    async (documentId: string, fallbackName: string) => {
      const { blob, filename } = await clientDocumentApi.download(documentId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || fallbackName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    [],
  );

  // Metadata status for "Data available" badge
  const [metadataStatus, setMetadataStatus] = useState<PolicyMetadataExistsResponse | null>(null);

  useProviderStatusHistory(policyId || '', !!policy && policy.completedLoaCount > 0);

  // Signatory removal state
  const [removingSignatory, setRemovingSignatory] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<PolicySignatoryDTO | null>(null);

  // Signatory status modal state
  const [resolveFlagSignatory, setResolveFlagSignatory] = useState<PolicySignatoryDTO | null>(null);
  const [rePublishSignatory, setRePublishSignatory] = useState<PolicySignatoryDTO | null>(null);

  // Override modal state
  const [overrideSignatory, setOverrideSignatory] = useState<PolicySignatoryDTO | null>(null);

  // Edit policy modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [submittingPolicy, setSubmittingPolicy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Document uploader toggle
  const [showUploader, setShowUploader] = useState(false);

  const loadPolicy = useCallback(async () => {
    if (!policyId) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await policyAggregateApi.getPolicyDetail(policyId);
      setPolicy(data);
    } catch (err) {
      console.error('Failed to load policy:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [policyId]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  // Fetch metadata status for "Data available" badge
  useEffect(() => {
    if (!policyId) return;
    policyMetadataApi.checkMetadataExists(policyId)
      .then(setMetadataStatus)
      .catch(() => { /* ignore */ });
  }, [policyId]);

  // Set breadcrumbs: Relationships / {RelationshipName} / Policies / {PolicyLabel}
  useEffect(() => {
    if (policy) {
      const policyLabel = policy.policyTypeName || policy.category || policy.policyNumber || policy.providerName || 'Policy';
      const relationshipName = policy.profileName || 'Relationship';
      setBreadcrumbItems([
        { label: 'Relationships', to: '/relationships' },
        { label: relationshipName, to: `/relationships/${policy.profileId}` },
        { label: 'Policies' },
        { label: policyLabel },
      ]);
      setBackTo(`/relationships/${policy.profileId}`);
    }
    return () => {
      setBreadcrumbItems(null);
      setBackTo(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally using granular fields to avoid re-running on unrelated policy changes
  }, [policy?.profileId, policy?.profileName, policy?.policyNumber, policy?.category, policy?.policyTypeName, policy?.providerName, setBreadcrumbItems, setBackTo]);

  const handleEditClick = () => {
    setEditError(null);
    setShowEditModal(true);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteSuccess = () => {
    setShowDeleteModal(false);
    if (policy) {
      navigate(`/relationships/${policy.profileId}`);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditError(null);
  };

  const handleEditSubmit = async (data: PolicyCreateDTO | PolicyUpdateDTO) => {
    if (!policy) return;
    try {
      setSubmittingPolicy(true);
      setEditError(null);
      await policyApi.updatePolicy(policy.profileId, policy.id, data as PolicyUpdateDTO);
      await loadPolicy();
      handleCloseEditModal();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Error updating policy:', error);
      setEditError(error.response?.data?.message || 'Failed to update policy');
    } finally {
      setSubmittingPolicy(false);
    }
  };

  // Convert PolicyDetailDTO to Policy shape for PolicyForm
  const policyForForm: Policy | undefined = policy ? {
    id: policy.id,
    profileId: policy.profileId,
    brokerageId: '',
    policyNumber: policy.policyNumber,
    category: policy.category,
    policyType: policy.policyTypeName,
    policyTypeId: policy.policyTypeId,
    policyTypeName: policy.policyTypeName,
    providerId: policy.providerId,
    providerName: policy.providerName,
    carrier: policy.providerName,
    premiumAmount: policy.premiumAmount ?? 0,
    status: policy.status as Policy['status'],
    effectiveDate: policy.effectiveDate,
    expiryDate: policy.expiryDate,
    ownershipType: policy.ownershipType as Policy['ownershipType'],
    signatories: [],
    createdBy: policy.createdBy,
    createdAt: policy.createdAt,
  } : undefined;

  const handleRemoveSignatory = async (signatory: PolicySignatoryDTO) => {
    if (!policyId) return;

    try {
      setRemovingSignatory(signatory.id);
      await policyAggregateApi.removeSignatory(policyId, signatory.id);
      await loadPolicy();
    } catch (err) {
      console.error('Failed to remove signatory:', err);
      setError(err as Error);
    } finally {
      setRemovingSignatory(null);
      setConfirmRemove(null);
    }
  };

  if (isLoading) {
    return (
      <div className="policy-detail-page">
        <div className="policy-loading">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="policy-detail-page">
        <Alert variant="error" header="Error Loading Policy">
          <p>{error.message || 'Failed to load policy details'}</p>
        </Alert>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="policy-detail-page">
        <Alert variant="warning" header="Policy Not Found">
          <p>The requested policy could not be found.</p>
        </Alert>
      </div>
    );
  }

  // Get primary client name for stats bar
  const primaryClient = policy.signatories?.find((s) => s.isPrimaryClient);
  const clientDisplayName = primaryClient
    ? `${primaryClient.firstName} ${primaryClient.lastName}`
    : policy.profileName || 'Unknown Client';

  const documentCount = versionCount
    + (providerSubmissions?.filter(s => s.submissionType === 'DOCUMENT').length || 0)
    + clientUploads.length;

  return (
    <div className="flex flex-col">
      {/* Combined Header + Tab Card */}
      <div className="bg-white flex flex-col px-lg pt-lg">
        <div className="flex w-full">
          {/* Provider Avatar */}
          <div className="policy-header__avatar mr-md mt-xs flex-shrink-0">
            {policy.providerLogoUrl ? (
              <img
                src={policy.providerLogoUrl}
                alt={policy.providerName}
                className="policy-header__avatar-img"
              />
            ) : (
              getProviderInitials(policy.providerName)
            )}
          </div>

          <div className="flex w-full flex-col">
            <div className="font-sans !text-h5 !font-medium leading-normal !pb-[6px] !m-0">
              {policy.category || policy.policyTypeName || '-'}
            </div>
            <div className="font-sans text-body-02 font-normal leading-normal text-grey-04">
              {policy.providerName} · {policy.policyNumber || 'No Policy Number'}
            </div>
            {policyId && (
              <div className="!mb-lg mt-sm">
                <EntityTagEditor entityType="POLICY" entityId={policyId} tags={policyTags} />
              </div>
            )}
          </div>

          <div className="flex items-center gap-lg flex-shrink-0">
            <div className="flex items-center gap-md">
            {/* Status tag */}
            {policy.providerRejected ? (
              <span className="inline-flex justify-center items-center gap-xs px-sm py-xs rounded-[4px] bg-error-bg text-error-text font-sans text-body-03 font-medium leading-normal overflow-hidden text-ellipsis">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                  <g clipPath="url(#clip0_2388_4740)">
                    <mask id="mask0_2388_4740" style={{maskType: 'luminance'}} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                      <path d="M16 0H0V16H16V0Z" fill="white"/>
                    </mask>
                    <g mask="url(#mask0_2388_4740)">
                      <path d="M8 9.83333C7.72667 9.83333 7.5 9.60667 7.5 9.33333V6C7.5 5.72667 7.72667 5.5 8 5.5C8.27333 5.5 8.5 5.72667 8.5 6V9.33333C8.5 9.60667 8.27333 9.83333 8 9.83333Z" fill="#96040E"/>
                      <path d="M7.9987 12.0003C7.9587 12.0003 7.91203 11.9937 7.86536 11.987C7.82536 11.9803 7.78536 11.967 7.74536 11.947C7.70536 11.9337 7.66536 11.9137 7.62536 11.887C7.59203 11.8603 7.5587 11.8337 7.52536 11.807C7.40536 11.6803 7.33203 11.507 7.33203 11.3337C7.33203 11.1603 7.40536 10.987 7.52536 10.8603C7.5587 10.8337 7.59203 10.807 7.62536 10.7803C7.66536 10.7537 7.70536 10.7337 7.74536 10.7203C7.78536 10.7003 7.82536 10.687 7.86536 10.6803C7.95203 10.6603 8.04536 10.6603 8.12536 10.6803C8.17203 10.687 8.21203 10.7003 8.25203 10.7203C8.29203 10.7337 8.33203 10.7537 8.37203 10.7803C8.40536 10.807 8.4387 10.8337 8.47203 10.8603C8.59203 10.987 8.66536 11.1603 8.66536 11.3337C8.66536 11.507 8.59203 11.6803 8.47203 11.807C8.4387 11.8337 8.40536 11.8603 8.37203 11.887C8.33203 11.9137 8.29203 11.9337 8.25203 11.947C8.21203 11.967 8.17203 11.9803 8.12536 11.987C8.08536 11.9937 8.0387 12.0003 7.9987 12.0003Z" fill="#96040E"/>
                      <path d="M12.039 14.7732H3.95901C2.65901 14.7732 1.66568 14.2999 1.15901 13.4465C0.659006 12.5932 0.725675 11.4932 1.35901 10.3532L5.39901 3.08656C6.06568 1.88656 6.98569 1.22656 7.99902 1.22656C9.01236 1.22656 9.93235 1.88656 10.599 3.08656L14.639 10.3599C15.2724 11.4999 15.3457 12.5932 14.839 13.4532C14.3324 14.2999 13.339 14.7732 12.039 14.7732ZM7.99902 2.22656C7.37236 2.22656 6.75902 2.70656 6.27234 3.57323L2.23901 10.8465C1.78568 11.6599 1.71234 12.4065 2.02568 12.9465C2.33901 13.4865 3.03234 13.7799 3.96568 13.7799H12.0457C12.979 13.7799 13.6657 13.4865 13.9857 12.9465C14.3057 12.4065 14.2257 11.6665 13.7724 10.8465L9.72569 3.57323C9.23902 2.70656 8.62569 2.22656 7.99902 2.22656Z" fill="#96040E"/>
                    </g>
                  </g>
                  <defs>
                    <clipPath id="clip0_2388_4740">
                      <rect width="16" height="16" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                LoA rejected
              </span>
            ) : metadataStatus?.hasProviderSubmission ? (
              <span className="inline-flex justify-center items-center gap-xs px-sm py-xs rounded-[4px] bg-success-bg text-success-text font-sans text-body-03 font-medium leading-normal overflow-hidden text-ellipsis">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                  <g clipPath="url(#clip0_2388_5593)">
                    <mask id="mask0_2388_5593" style={{maskType: 'luminance'}} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                      <path d="M16 0H0V16H16V0Z" fill="white"/>
                    </mask>
                    <g mask="url(#mask0_2388_5593)">
                      <path d="M9.9987 15.1667H5.9987C2.3787 15.1667 0.832031 13.62 0.832031 10V6C0.832031 2.38 2.3787 0.833336 5.9987 0.833336H9.9987C13.6187 0.833336 15.1654 2.38 15.1654 6V10C15.1654 13.62 13.6187 15.1667 9.9987 15.1667ZM5.9987 1.83334C2.92536 1.83334 1.83203 2.92667 1.83203 6V10C1.83203 13.0733 2.92536 14.1667 5.9987 14.1667H9.9987C13.072 14.1667 14.1654 13.0733 14.1654 10V6C14.1654 2.92667 13.072 1.83334 9.9987 1.83334H5.9987Z" fill="#004E24"/>
                      <path d="M7.05294 10.3864C6.91961 10.3864 6.79294 10.3331 6.69961 10.2397L4.81297 8.35307C4.61964 8.15974 4.61964 7.83974 4.81297 7.6464C5.0063 7.45307 5.3263 7.45307 5.51964 7.6464L7.05294 9.17974L10.4796 5.75308C10.6729 5.55974 10.9929 5.55974 11.1863 5.75308C11.3796 5.94641 11.3796 6.26641 11.1863 6.45974L7.40628 10.2397C7.31294 10.3331 7.18628 10.3864 7.05294 10.3864Z" fill="#004E24"/>
                    </g>
                  </g>
                  <defs>
                    <clipPath id="clip0_2388_5593">
                      <rect width="16" height="16" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                Data available
              </span>
            ) : policy.completedLoaCount > 0 ? (
              <span className="inline-flex justify-center items-center gap-xs px-sm py-xs rounded-[4px] bg-warning-bg text-warning-text font-sans text-body-03 font-medium leading-normal overflow-hidden text-ellipsis">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                  <g clipPath="url(#clip0_2388_5208)">
                    <mask id="mask0_2388_5208" style={{maskType: 'luminance'}} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                      <path d="M16 0H0V16H16V0Z" fill="white"/>
                    </mask>
                    <g mask="url(#mask0_2388_5208)">
                      <path d="M7.9987 15.1667C4.04536 15.1667 0.832031 11.9533 0.832031 8C0.832031 4.04667 4.04536 0.833332 7.9987 0.833332C11.952 0.833332 15.1654 4.04667 15.1654 8C15.1654 11.9533 11.952 15.1667 7.9987 15.1667ZM7.9987 1.83333C4.5987 1.83333 1.83203 4.6 1.83203 8C1.83203 11.4 4.5987 14.1667 7.9987 14.1667C11.3987 14.1667 14.1654 11.4 14.1654 8C14.1654 4.6 11.3987 1.83333 7.9987 1.83333Z" fill="#733F01"/>
                      <path d="M10.4711 10.6199C10.3845 10.6199 10.2978 10.5999 10.2178 10.5465L8.15115 9.3132C7.63781 9.00654 7.25781 8.3332 7.25781 7.73987V5.00652C7.25781 4.73318 7.48448 4.50652 7.75781 4.50652C8.03115 4.50652 8.25781 4.73318 8.25781 5.00652V7.73987C8.25781 7.97987 8.45781 8.3332 8.66448 8.4532L10.7311 9.68654C10.9711 9.82654 11.0445 10.1332 10.9045 10.3732C10.8045 10.5332 10.6378 10.6199 10.4711 10.6199Z" fill="#733F01"/>
                    </g>
                  </g>
                  <defs>
                    <clipPath id="clip0_2388_5208">
                      <rect width="16" height="16" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                Pending provider
              </span>
            ) : policy.loaCount > 0 ? (
              <span className="inline-flex justify-center items-center gap-xs px-sm py-xs rounded-[4px] bg-warning-bg text-warning-text font-sans text-body-03 font-medium leading-normal overflow-hidden text-ellipsis">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                  <g clipPath="url(#clip0_2388_5632)">
                    <mask id="mask0_2388_5632" style={{maskType: 'luminance'}} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                      <path d="M16 0H0V16H16V0Z" fill="white"/>
                    </mask>
                    <g mask="url(#mask0_2388_5632)">
                      <path d="M11.332 14.1667H4.66536C2.23203 14.1667 0.832031 12.7667 0.832031 10.3333V5.66668C0.832031 3.23334 2.23203 1.83334 4.66536 1.83334H11.332C13.7654 1.83334 15.1654 3.23334 15.1654 5.66668V10.3333C15.1654 12.7667 13.7654 14.1667 11.332 14.1667ZM4.66536 2.83334C2.7587 2.83334 1.83203 3.76001 1.83203 5.66668V10.3333C1.83203 12.24 2.7587 13.1667 4.66536 13.1667H11.332C13.2387 13.1667 14.1654 12.24 14.1654 10.3333V5.66668C14.1654 3.76001 13.2387 2.83334 11.332 2.83334H4.66536Z" fill="#733F01"/>
                      <path d="M7.99921 8.58C7.43921 8.58 6.87255 8.40667 6.4392 8.05333L4.35254 6.38665C4.1392 6.21331 4.0992 5.89998 4.27254 5.68665C4.44587 5.47331 4.7592 5.43331 4.97254 5.60665L7.05922 7.27333C7.56588 7.68 8.42588 7.68 8.93255 7.27333L11.0192 5.60665C11.2325 5.43331 11.5525 5.46665 11.7192 5.68665C11.8925 5.89998 11.8592 6.21998 11.6392 6.38665L9.55255 8.05333C9.12588 8.40667 8.55921 8.58 7.99921 8.58Z" fill="#733F01"/>
                    </g>
                  </g>
                  <defs>
                    <clipPath id="clip0_2388_5632">
                      <rect width="16" height="16" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                LoA invite sent
              </span>
            ) : null}

            {/* Ownership type tag */}
            <span className="inline-flex justify-center items-center gap-xs px-sm py-xs rounded-[4px] bg-neutral-bg text-neutral-text font-sans text-body-03 font-medium leading-normal overflow-hidden text-ellipsis">
              {policy.ownershipType === 'JOINT' ? 'Joint' : 'Solo'}
            </span>

            {/* Agency status tag */}
            <span className={`inline-flex justify-center items-center gap-xs px-sm py-xs rounded-[4px] font-sans text-body-03 font-medium leading-normal overflow-hidden text-ellipsis ${
              policy.inAgency
                ? 'bg-success-bg text-success-text'
                : 'bg-grey-06 text-grey-04'
            }`}>
              {policy.inAgency ? 'In Agency' : 'Not in Agency'}
            </span>

            {/* LOA in Place tag */}
            <span
              className={`inline-flex justify-center items-center gap-xs px-sm py-xs rounded-[4px] font-sans text-body-03 font-medium leading-normal overflow-hidden text-ellipsis ${
                policy.loaInPlace
                  ? 'bg-success-bg text-success-text'
                  : 'bg-grey-06 text-grey-04'
              }`}
              title={policy.loaInPlace && policy.loaInPlaceSince
                ? `In place since ${new Date(policy.loaInPlaceSince).toLocaleDateString()}`
                : undefined}
            >
              {policy.loaInPlace ? 'LoA in Place' : 'LoA Not in Place'}
            </span>
            </div>

            <MoreOptionsButton onEdit={handleEditClick} onDelete={handleDeleteClick} />
          </div>
        </div>


        <TabBar
          tabs={[
            { key: 'overview', label: 'Overview' },
            { key: 'metadata', label: 'Metadata', count: metadataStatus?.hasProviderSubmission ? 1 : undefined },
            { key: 'loas', label: 'Client Signed Documents' },
            { key: 'documents', label: 'Documents', count: documentCount > 0 ? documentCount : undefined },
            { key: 'log', label: 'Log' },
          ]}
          activeTab={activeTab}
          onTabChange={(key) => setActiveTab(key as 'overview' | 'metadata' | 'loas' | 'documents' | 'log')}
        />
      </div>

      {/* Tab Content */}
      <div className="bg-page-bg p-lg min-h-[calc(100vh-80px)] overflow-auto">
      {activeTab === 'overview' && (
        <>
          {/* LOA Rejection Panel */}
          {policy.providerRejected && (
            <div style={{ marginBottom: 24 }}>
              <AlertInfoPanel
                variant="error"
                title="Letter of Authority rejected by the provider"
                fields={[
                  {
                    label: 'Reason for Letter of Authority rejection.',
                    value: getRejectionReasonLabel(policy.providerRejectionReasonCode),
                    required: true,
                  },
                  {
                    label: 'Additional information',
                    value: policy.providerRejectionReason,
                    multiline: true,
                  },
                ]}
              />
            </div>
          )}

          {/* Policy Information Card */}
          <div data-testid="policy-information-card" className="rounded-card bg-white overflow-hidden mb-lg shadow-[0_0_10px_rgba(0,0,0,0.1)]">
            <div className="loa-table-card__header border-b border-[#E1E1E1] bg-white">
              <h3 className="loa-table-card__title">Policy information</h3>
            </div>
            <div className="px-md py-lg flex gap-lg">
              <div className="flex-1 flex flex-col gap-sm">
                <div className="font-sans text-body-02 font-medium leading-normal text-[#000]">Policy Holder Name</div>
                <div className="font-sans text-body-02 font-normal leading-normal text-[#000]">
                  {policy.resolvedPolicyHolderName || clientDisplayName}
                </div>
              </div>
              <div className="flex-1 flex flex-col gap-sm">
                <div className="font-sans text-body-02 font-medium leading-normal text-[#000]">Address on the policy</div>
                <div className="font-sans text-body-02 font-normal leading-normal text-[#000] whitespace-pre-line">
                  {policy.resolvedAddress
                    ? policy.resolvedAddress.split('\n').map((line, i, arr) => (
                        <span key={i}>
                          {line}
                          {i < arr.length - 1 && <br />}
                        </span>
                      ))
                    : '-'}
                </div>
              </div>
              <div className="flex-1 flex flex-col gap-sm">
                <div className="font-sans text-body-02 font-medium leading-normal text-[#000]">Current Value</div>
                <div className="font-sans text-body-02 font-normal leading-normal text-[#000]">
                  {formatCurrency(policy.resolvedCurrentValue ?? policy.premiumAmount)}
                </div>
              </div>
              <div className="flex-1 flex flex-col gap-sm">
                <div className="font-sans text-body-02 font-medium leading-normal text-[#000]">Date of Current Value</div>
                <div className="font-sans text-body-02 font-normal leading-normal text-[#000]">
                  {formatDate(policy.resolvedCurrentValueDate ?? policy.effectiveDate) || '-'}
                </div>
              </div>
            </div>
          </div>

          {/* Clients & Signatories Table */}
          <div className="policy-signatories-card">
            <h3 className="policy-signatories-card__title">Clients & Signatories</h3>
            {!policy.signatories || policy.signatories.length === 0 ? (
              <div className="policy-message">
                <Alert variant="info" header="No Clients Associated">
                  <p>
                    This policy has no clients or signatories assigned yet.
                    Go to the <Link to={`/relationships/${policy.profileId}`}>relationship</Link> to manage policy relationships.
                  </p>
                </Alert>
              </div>
            ) : (
              <table className="policy-signatories-table">
                <thead>
                  <tr>
                    <th>Client name</th>
                    <th>Email</th>
                    <th>Relationship</th>
                    <th>Retirement Date (NRA)</th>
                    <th>LoA Status</th>
                    <th>Added</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {policy.signatories.map((signatory) => (
                    <tr key={signatory.id}>
                      <td>
                        <div className="policy-client-cell">
                          <Link to={`/clients/${signatory.clientId}`} className="policy-client-cell__name">
                            {signatory.firstName} {signatory.lastName}
                          </Link>
                          {signatory.isPrimaryClient && (
                            <span className="policy-client-cell__primary">Primary</span>
                          )}
                        </div>
                      </td>
                      <td className="policy-signatories-table__email">{signatory.email}</td>
                      <td>
                        <span className={`policy-relationship-badge policy-relationship-badge--${getRelationshipClass(signatory.relationshipType)}`}>
                          {formatRelationshipType(signatory.relationshipType)}
                        </span>
                      </td>
                      <td>-</td>
                      <td>
                        {signatory.signatoryStatus ? (
                          <SignatoryStatusBadge status={signatory.signatoryStatus} size="small" />
                        ) : (
                          <span className="policy-signatories-table__empty">-</span>
                        )}
                      </td>
                      <td>{formatDate(signatory.addedAt)}</td>
                      <td>
                        <div className="policy-signatories-table__actions">
                          {/* Override action */}
                          <button
                            type="button"
                            className={`policy-signatories-table__action-btn ${signatory.hasOverride ? 'policy-signatories-table__action-btn--active' : ''}`}
                            title={signatory.hasOverride ? 'View or edit the detail override for this signatory' : 'Set alternative details for this signatory on this policy'}
                            onClick={() => setOverrideSignatory(signatory)}
                          >
                            <PolicyOverrideIndicator
                              hasOverride={signatory.hasOverride}
                              overriddenFields={signatory.override?.overriddenFields}
                              reason={signatory.override?.reason}
                            />
                          </button>

                          {/* Status-based actions */}
                          {signatory.signatoryStatus === 'FLAGGED' && (
                            <Button
                              variant="outlined"
                              size="sm"
                              title="Resolve the flag and return signatory to Ready for Signing"
                              onClick={() => setResolveFlagSignatory(signatory)}
                            >
                              <AppIcon name="check circle" />
                              Resolve Flag
                            </Button>
                          )}
                          {(signatory.signatoryStatus === 'CONFIRMED' ||
                            signatory.signatoryStatus === 'PENDING_SIGNATURE' ||
                            signatory.signatoryStatus === 'SIGNED') && (
                            <Button
                              variant="outlined"
                              size="sm"
                              title="Request client to review and verify policy details"
                              onClick={() => setRePublishSignatory(signatory)}
                            >
                              <AppIcon name="eye" />
                              Request Review
                            </Button>
                          )}

                          {/* Remove action */}
                          {signatory.canRemove ? (
                            <button
                              type="button"
                              className="policy-signatories-table__delete-btn"
                              title="Remove this client's relationship with the policy"
                              disabled={removingSignatory !== null}
                              onClick={() => setConfirmRemove(signatory)}
                            >
                              <AppIcon name="trash outline" size={16} />
                            </button>
                          ) : (
                            <span
                              className="policy-signatories-table__locked"
                              title={signatory.isPrimaryClient ? 'Primary client cannot be removed from policy' : 'This relationship cannot be removed'}
                            >
                              <AppIcon name="lock" /> Cannot remove
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {activeTab === 'metadata' && policyId && (
          <PolicyMetadataTab
            policyId={policyId}
            policyNumber={policy.policyNumber || 'Unknown'}
            policyCategory={policy.category || policy.policyTypeName || ''}
            providerId={policy.providerId || ''}
            brokerageId={advisor?.brokerageId?.toString() || ''}
          />
      )}

      {activeTab === 'loas' && policyId && (
          <PolicySignedDocumentsTab policyId={policyId} />
      )}

      {activeTab === 'documents' && policyId && (
        <>
          <PolicyDocumentsTable
            versions={documentVersions}
            providerDocuments={providerSubmissions || []}
            clientUploads={clientUploads}
            loading={documentsLoading || providerSubmissionsLoading || clientUploadsLoading}
            error={documentsError}
            onDownloadVersion={downloadVersion}
            onDownloadProviderDoc={downloadProviderDocument}
            onDownloadClientUpload={handleDownloadClientUpload}
            onViewDocument={handleViewDocument}
            onUploadClick={() => setShowUploader(true)}
            currentAdvisorName={advisor ? `${advisor.firstName} ${advisor.lastName}` : undefined}
            currentAdvisorKeycloakId={advisor?.keycloakUserId}
          />

          {showUploader && (
            <div className="policy-content-card" style={{ marginTop: 24 }}>
              <div className="policy-content-card__header">
                <h3 className="policy-content-card__title">Upload New Document</h3>
                <Button variant="outlined" size="sm" onClick={() => setShowUploader(false)}>
                  <AppIcon name="close" /> Close
                </Button>
              </div>
              <PolicyDocumentUploader
                policyId={policyId}
                onUploadComplete={() => {
                  refreshDocuments();
                  setShowUploader(false);
                }}
                onUploadError={(err) => {
                  console.error('Upload error:', err);
                }}
                disabled={!hasDocuments && documentStatus === PolicyDocumentStatus.AWAITING_LOA}
              />
              {documentStatus === PolicyDocumentStatus.AWAITING_LOA && !hasDocuments && (
                <Alert variant="info">
                  Documents can be uploaded after an LOA has been completed for this policy.
                </Alert>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'log' && policyId && (
        <ProviderSubmissionsTab policyId={policyId} resolveUser={resolveUser} />
      )}
      </div>

      <ViewLoAModal
        open={!!viewDocState}
        onClose={handleCloseDocViewer}
        documentUrl={viewDocUrl}
        title={viewDocState?.title || ''}
        loading={viewDocLoading}
        downloadFilename={viewDocState?.title}
      />

      {/* Resolve Flag Modal */}
      {resolveFlagSignatory && (
        <ResolveFlagModal
          open={!!resolveFlagSignatory}
          onClose={() => setResolveFlagSignatory(null)}
          onSuccess={loadPolicy}
          profileId={policy.profileId}
          policyId={policy.id}
          signatoryId={resolveFlagSignatory.id}
          policyNumber={policy.policyNumber || 'N/A'}
          clientName={`${resolveFlagSignatory.firstName} ${resolveFlagSignatory.lastName}`}
          flagReason={resolveFlagSignatory.flagReason}
          flagCreatedAt={resolveFlagSignatory.flagCreatedAt}
        />
      )}

      {/* Re-publish Modal */}
      {rePublishSignatory && (
        <RePublishModal
          open={!!rePublishSignatory}
          onClose={() => setRePublishSignatory(null)}
          onSuccess={loadPolicy}
          profileId={policy.profileId}
          policyId={policy.id}
          signatoryId={rePublishSignatory.id}
          policyNumber={policy.policyNumber || 'N/A'}
          clientName={`${rePublishSignatory.firstName} ${rePublishSignatory.lastName}`}
          currentStatus={rePublishSignatory.signatoryStatus || ''}
        />
      )}

      {/* Override Modal */}
      {overrideSignatory && (
        <PolicyOverrideModal
          open={!!overrideSignatory}
          onClose={() => setOverrideSignatory(null)}
          onSuccess={loadPolicy}
          profileId={policy.profileId}
          policyId={policy.id}
          signatoryId={overrideSignatory.id}
          policyNumber={policy.policyNumber || 'N/A'}
          clientName={`${overrideSignatory.firstName} ${overrideSignatory.lastName}`}
          existingOverride={overrideSignatory.override}
          clientProfile={{
            firstName: overrideSignatory.firstName,
            lastName: overrideSignatory.lastName,
            email: overrideSignatory.email,
          }}
        />
      )}

      {/* Confirmation Dialog */}
      <FinModal open={confirmRemove !== null} onClose={() => setConfirmRemove(null)} size="small">
        <FinModal.Header title="Remove Client from Policy" onClose={() => setConfirmRemove(null)} />
        <FinModal.Content>
          {confirmRemove && (
            <p>
              Are you sure you want to remove {confirmRemove.firstName} {confirmRemove.lastName} from this policy? This will remove their {confirmRemove.relationshipType.replaceAll('_', ' ').toLowerCase()} relationship.
            </p>
          )}
        </FinModal.Content>
        <FinModal.Actions
          primaryAction={{
            label: 'Remove',
            onClick: () => {
              if (confirmRemove) void handleRemoveSignatory(confirmRemove);
            },
            colour: 'red',
          }}
          secondaryAction={{
            label: 'Cancel',
            onClick: () => setConfirmRemove(null),
          }}
        />
      </FinModal>

      {/* Edit Policy Modal — Combined modal for SOLO, old PolicyForm for JOINT/GROUP */}
      {showEditModal && policy && (policy.signatories?.length === 1 || policy.ownershipType === 'SOLO') && policy.signatories?.[0] && (
        <CombinedPolicyEditModal
          open={showEditModal}
          onClose={handleCloseEditModal}
          onSuccess={loadPolicy}
          policy={policy}
          signatory={policy.signatories[0]}
          profileId={policy.profileId}
        />
      )}
      {showEditModal && policy && policy.signatories?.length !== 1 && policy.ownershipType !== 'SOLO' && (
        <FinModal open={showEditModal} onClose={handleCloseEditModal} size="medium">
          <FinModal.Header title="Edit Policy" onClose={handleCloseEditModal} />
          <FinModal.Content>
            <PolicyForm
              policy={policyForForm}
              onSubmit={handleEditSubmit}
              onCancel={handleCloseEditModal}
              loading={submittingPolicy}
              error={editError}
            />
          </FinModal.Content>
        </FinModal>
      )}

      {/* Delete Policy Confirmation Modal */}
      {showDeleteModal && policyId && policy && (
        <DeletePolicyModal
          open={showDeleteModal}
          profileId={policy.profileId}
          policyId={policyId}
          onClose={() => setShowDeleteModal(false)}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
}
